import React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { ErrorMessage } from "formik";
import { TextInputComponentProps } from "./text-input-component.props";
import "./text-input-component.scss";
import InputAdornment from "@mui/material/InputAdornment";
import lockIcon from "../../../assets/icons/lock-icon.svg";

/**
 * TextInputComponent wraps MUI TextField with input sanitization helpers
 * (email/name/alphanumeric/street/number), lock icon adornment, and Formik errors.
 */
const TextInputComponent: React.FC<TextInputComponentProps> = ({
  fieldName = "",
  label,
  placeholder = "",
  optional = false,
  disabled,
  value,
  type,
  onChange,
  minlength,
  maxlength,
  isRequired = false,
  isName = false,
  isAlphaNumeric = false,
  allowSpecialCharacters = false,
  handleBlur,
  isNumber = false,
  blockInvalidChars,
  isStreet,
  multiline,
  rows,
  isLocked = false,
  lockedIcons,
  isShowError = false,
  autoComplete,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // block 'e', 'E', '+', '-', etc.
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };
  const handleInput = (event: any) => {
    let value = event.target.value;
    if (type === "email") {
      const sanitizedValue = value.replaceAll(/[^A-Za-z0-9._%+\-@]/g, "");
      const [localPart, domainPart] = sanitizedValue.split("@");

      if (domainPart !== undefined) {
        if (domainPart.includes("..")) {
          value = `${localPart}@${domainPart.replaceAll(/\.+/g, ".")}`;
        } else if (domainPart.split(".").length > 2) {
          const parts = domainPart.split(".");
          const extension = parts.pop();
          const domain = parts.join(".");
          value = `${localPart}@${domain}.${extension?.slice(0, 6)}`;
        }
      }
    } else if (isName) {
      event.target.value = value.replaceAll(/[^A-Za-z -]/g, "");
    } else if (isAlphaNumeric) {
      event.target.value = value.replaceAll(/[^A-Za-z0-9 ]/g, "");
    } else if (allowSpecialCharacters) {
      event.target.value = value.replaceAll(/[^A-Za-z0-9\s&.,'/-]/g, "");
    } else if (isStreet) {
      event.target.value = value.replaceAll(/[^A-Za-z0-9\s_,+/-]/g, "");
    } else if (isNumber) {
      const digitsOnly = value.replaceAll(/[^\d]/g, "");
      event.target.value = digitsOnly.slice(0, Number(maxlength));
    }
    if (type === "number" && Number(value) < 0) {
      event.target.value = "";
    }
  };
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (value === 0 || value === "0" || value === "0.0" || value === "0.00") {
      event.target.value = "";
      const syntheticEvent = {
        ...event,
        target: { ...event.target, value: "" },
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <Box>
      <div
        className={`form-text-input-component ${isRequired ? "required" : ""}`}
      >
        {label && (
          <InputLabel required={optional} htmlFor={fieldName}>
            {label}
          </InputLabel>
        )}
        <FormControl>
          <TextField
            variant="outlined"
            name={fieldName}
            placeholder={placeholder}
            required={optional}
            disabled={disabled}
            id={fieldName}
            value={value}
            multiline={multiline}
            rows={rows}
            onChange={onChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            autoComplete={autoComplete}
            onInput={handleInput}
            type={type}
            sx={{
              "& .MuiInputBase-input::placeholder": {
                color: isShowError ? "#dc3545" : "#aaa",
                opacity: 1,
              },
            }}
            onKeyDown={blockInvalidChars ? handleKeyDown : undefined}
            slotProps={{
              htmlInput: {
                minLength: minlength,
                maxLength: maxlength,
              },
              input: {
                endAdornment: isLocked ? (
                  <InputAdornment position="end">
                    {lockedIcons || (
                      <img
                        src={lockIcon}
                        alt="lock icon"
                        style={{ height: "20px", width: "20px" }}
                      />
                    )}
                  </InputAdornment>
                ) : null,
                readOnly: isLocked && !lockedIcons, // Optional: prevent typing if locked
              },
            }}
          />
        </FormControl>
        <ErrorMessage
          name={fieldName || ""}
          component="div"
          className="error-message"
        />
      </div>
    </Box>
  );
};

export default TextInputComponent;
